import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    supplierRelationshipDetailUrl,
    supplierRelationshipListUrl,
    supplierRelationshipUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import type {SupplierRelationship} from "~/pages/supplier-360/types/Supplier360Types";

export type UpdateSupplierParentVariables = {
    entityId: number;
    relationship: SupplierRelationship | undefined;
    to_name: string;
    /** The parent's own entity id when it is a resolved supplier, null for a free-text name (§2.4 — `to_entity` is nullable). */
    to_entity?: number | null;
};

/**
 * Create / update / clear the Overview "Parent" row.
 * Parent is the `subsidiary` edge (`to_name`) — docs §2.4 full CRUD, §4.4.
 */
export function useUpdateSupplierParentMutation() {
    const queryClient = useQueryClient();

    return useMutation<SupplierRelationship | null, DefaultError, UpdateSupplierParentVariables>({
        mutationFn: async ({entityId, relationship, to_name, to_entity = null}) => {
            const name = to_name.trim();

            if (relationship) {
                if (!name) {
                    await queryHttp.delete(supplierRelationshipDetailUrl(relationship.id)); // TODO: #business is this correct "delete parent"?
                    return null;
                }
                const {data} = await queryHttp.patch<SupplierRelationship>(
                    supplierRelationshipDetailUrl(relationship.id),
                    {to_name: name, to_entity},
                );
                return data;
            }

            if (!name) return null;

            const {data} = await queryHttp.post<SupplierRelationship>(
                supplierRelationshipListUrl(),
                {
                    from_entity: entityId,
                    to_entity,
                    to_name: name,
                    relationship_type: 'subsidiary', // TODO: #business is this correct "parent === subsidiary"?
                    confidence: 1,
                    evidence_source: 'manual',
                },
            );
            return data;
        },
        onSuccess: (_updated, {entityId, relationship, to_name}) => {
            void queryClient.invalidateQueries({queryKey: [supplierRelationshipUrl(entityId)]});

            // No-op when there was nothing to clear.
            if (!relationship && !to_name.trim()) return;

            const removed = Boolean(relationship) && !to_name.trim();
            notificationStore.show(removed ? 'Parent removed.' : 'Parent updated.', {
                severity: 'success',
                id: `supplier-parent-success-${entityId}`,
                autoHideDuration: 3000,
            });
        },
        onError: (_error, {entityId}) => {
            notificationStore.show('Could not save parent. Please try again.', {
                severity: 'error',
                id: `supplier-parent-error-${entityId}`,
                autoHideDuration: 4000,
            });
        },
    });
}
